import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Layers, Wallet, LayoutList } from "lucide-react";
import {
  getExpenseCategoriesApi,
  getOfficeWalletHistoryApi,
  listOfficeExpensesApi,
} from "../../app/api";

export default function ExpenseKpiStrip() {
  const location = useLocation();
  const navigate = useNavigate();

  const [counts, setCounts] = useState({
    categories: 0,
    walletCount: 0,
    expenses: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchCounts = async () => {
    try {
      setLoading(true);
      const [catRes, walletRes, expRes] = await Promise.all([
        getExpenseCategoriesApi(),
        getOfficeWalletHistoryApi(),
        listOfficeExpensesApi(),
      ]);

      const catCount = catRes?.success ? (catRes.data?.length || 0) : 0;
      const walletCount = walletRes?.success ? (walletRes.data?.length || 0) : 0;
      const expCount = expRes?.success ? (expRes.data?.length || 0) : 0;

      setCounts({
        categories: catCount,
        walletCount,
        expenses: expCount,
      });
    } catch (err) {
      console.error("Error fetching expense counts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();

    // Listen to custom update events
    window.addEventListener("expense-updated", fetchCounts);
    return () => {
      window.removeEventListener("expense-updated", fetchCounts);
    };
  }, []);

  const scrollToList = () => {
    setTimeout(() => {
      const el = document.getElementById("expense-list-section");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
  };

  const handleCardClick = (path) => {
    if (location.pathname === path) {
      scrollToList();
    } else {
      navigate(path);
      scrollToList();
    }
  };

  const kpis = [

  {
      label: " Expenses List",
      value: counts.expenses,
      subtext: "Logs ever registered",
      icon: LayoutList,
      bg: "bg-[#E0F2FE]", // Light blue
      iconBg: "bg-blue-200/50",
      iconColor: "text-[#0EA5E9]",
      textColor: "text-blue-900",
      subtextColor: "text-blue-600/80",
      onClick: () => handleCardClick("/expenses"),
      path: "/expenses",
    },
 
    {
      label: "Wallet Entries",
      value: counts.walletCount,
      subtext: "Wallet transactions logged",
      icon: Wallet,
      bg: "bg-[#E0FBEA]", // Light green/emerald
      iconBg: "bg-green-200/50",
      iconColor: "text-[#22C55E]",
      textColor: "text-green-900",
      subtextColor: "text-green-600/80",
      onClick: () => handleCardClick("/expenses/wallet"),
      path: "/expenses/wallet",
    },
  

   {
      label: "Expense Categories",
      value: counts.categories,
      subtext: "Categorized classifications",
      icon: Layers,
      bg: "bg-[#F3E8FF]", // Light purple
      iconBg: "bg-purple-200/50",
      iconColor: "text-[#9333EA]",
      textColor: "text-purple-900",
      subtextColor: "text-purple-600/80",
      onClick: () => handleCardClick("/expenses/categories"),
      path: "/expenses/categories",
    },
  ];

  if (loading) {
    return (
      <div className="relative z-20 mt-4 sm:mt-[-20px] px-4 sm:px-6 max-w-6xl mx-auto w-full">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-36 bg-white/10 rounded-2xl border border-white/10" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative z-20 mt-4 sm:mt-[-20px] px-4 sm:px-6 max-w-6xl mx-auto w-full">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5">
        {kpis.map((kpi, idx) => {
          const isActive = location.pathname === kpi.path;
          return (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: idx * 0.08,
                duration: 0.45,
                type: "spring",
                stiffness: 160,
              }}
              whileHover={{
                y: -8,
                scale: 1.03,
                rotateX: 4,
                rotateY: -4,
              }}
              onClick={kpi.onClick}
              className={`
                ${kpi.bg}
                rounded-2xl
                p-5
                relative
                overflow-hidden
                border-2
                ${isActive ? "border-indigo-600 shadow-[0_0_20px_rgba(99,102,241,0.5)] scale-[1.02]" : "border-white/50"}
                shadow-[0_20px_50px_rgba(45,28,106,0.3)]
                hover:shadow-[0_30px_70px_rgba(45,28,106,0.55)]
                group
                cursor-pointer
                flex flex-col justify-between
                h-36
                transition-all duration-300
                active:scale-95
              `}
              style={{ transformStyle: "preserve-3d" }}
            >
              {/* Animated Light Sweep */}
              <div className="
                absolute -inset-10
                bg-gradient-to-r
                from-transparent
                via-white/20
                to-transparent
                opacity-0
                group-hover:opacity-100
                transition
                duration-700
                rotate-12
                pointer-events-none
              " />

              {/* Header: Icon & Active Indicator */}
              <div className="flex items-center justify-between">
                <div className={`w-10 h-10 rounded-xl ${kpi.iconBg} flex items-center justify-center shadow-sm ring-1 ring-white/50`}>
                  <kpi.icon className={kpi.iconColor} size={20} />
                </div>
                {isActive ? (
                  <span className="text-[10px] font-black uppercase text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full shadow-sm">
                    Active
                  </span>
                ) : (
                  <span className={`w-2 h-2 rounded-full ${kpi.iconColor} opacity-70 animate-pulse`} />
                )}
              </div>

              {/* Labels and values */}
              <div className="mt-3">
                <p className={`${kpi.textColor} opacity-70 text-xs font-bold uppercase tracking-wider`}>
                  {kpi.label}
                </p>
                <h3 className={`text-2xl font-black mt-0.5 tracking-tight ${kpi.textColor}`}>
                  {kpi.value}
                </h3>
                <p className={`text-[10px] mt-0.5 font-semibold ${kpi.subtextColor}`}>
                  {kpi.subtext}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
